'use client';

import Image from 'next/image';
import type { Service } from './types';
import type { ToolsTranslations } from '@/lib/translations/types';

interface Props {
  service: Service;
  translations: ToolsTranslations['autoInvesting'];
  /**
   * Optional override for the secondary "review" CTA on the right side of
   * the action row. When provided, replaces both the service's review URL
   * and label — used by the SingleService view to point readers to a
   * comparison/alternatives page instead of the service's own review.
   */
  reviewCtaOverride?: { label: string; url: string };
}

const STAR_SVG = (filled: boolean) => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill={filled ? '#f59e0b' : 'none'}
    stroke={filled ? '#f59e0b' : '#e5e7eb'}
    strokeWidth={filled ? 0 : 1.5}
    aria-hidden="true"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

export default function ServiceCard({
  service,
  translations,
  reviewCtaOverride,
}: Props) {
  const t = translations;

  const ACCENT = '#1d4ed8';

  // Pick the secondary CTA: an explicit override wins, otherwise fall back
  // to the service's own review URL/label.
  const reviewCta = reviewCtaOverride
    ? reviewCtaOverride
    : service.reviewCtaUrl
      ? { url: service.reviewCtaUrl, label: service.reviewCtaLabel || t.readReview }
      : null;

  // A metric is "empty" when the upstream simply doesn't have data for it.
  // Treat dashes/whitespace as empty so blank cards don't render half-filled
  // boxes.
  const isEmpty = (s: string | null | undefined) => {
    if (!s) return true;
    const trimmed = s.trim();
    return trimmed === '' || trimmed === '—' || trimmed === '-';
  };

  type MetricItem = { label: string; value: string; note?: string; tooltip?: string };
  const metrics: MetricItem[] = [];

  if (!isEmpty(service.totalFeeValue)) {
    metrics.push({
      label: t.metrics.totalFee,
      value: service.totalFeeValue,
      note: service.totalFeeDescription,
    });
  }
  if (!isEmpty(service.minDepositValue)) {
    metrics.push({
      label: t.metrics.minDeposit,
      value: service.minDepositValue,
      note: service.minDepositDescription,
    });
  }
  if (service.hasHistoricalReturn && !isEmpty(service.historicalReturnValue)) {
    metrics.push({
      label: t.metrics.historicalReturn,
      value: service.historicalReturnValue,
      note: service.historicalReturnDescription,
      tooltip: t.historicalReturnTooltip,
    });
  }
  if (!isEmpty(service.riskLevelsValue)) {
    metrics.push({
      label: t.metrics.riskLevels,
      value: service.riskLevelsValue,
      note: service.riskLevelsDescription,
    });
  }
  if (!isEmpty(service.withdrawalTimeValue)) {
    metrics.push({
      label: t.metrics.withdrawalTime,
      value: service.withdrawalTimeValue,
      note: service.withdrawalTimeDescription,
    });
  }

  // Match grid columns to actual content. Cap at lg-cols-3 so each metric
  // box stays roomy enough for the longer labels we use here ("Historisk
  // avkastning", "Withdrawal time" etc).
  const lgColsClass =
    metrics.length >= 3
      ? 'lg:grid-cols-3'
      : metrics.length === 2
      ? 'lg:grid-cols-2'
      : 'lg:grid-cols-1';

  // Build chip list once so we can render it conditionally.
  const featureChips: { id: string; label: string }[] = [];
  if (service.monthlySavings)
    featureChips.push({ id: 'monthlySavings', label: t.featureChips.monthlySavings });
  if (service.sustainable)
    featureChips.push({ id: 'sustainable', label: t.featureChips.sustainable });
  if (service.autoRebalancing)
    featureChips.push({ id: 'autoRebalancing', label: t.featureChips.autoRebalancing });
  if (service.taxOptimization)
    featureChips.push({ id: 'taxOptimization', label: t.featureChips.taxOptimization });
  if (service.bankId) featureChips.push({ id: 'bankId', label: t.featureChips.bankId });
  if (service.mobileApp)
    featureChips.push({ id: 'mobileApp', label: t.featureChips.mobileApp });
  if (service.humanAdvisor)
    featureChips.push({ id: 'humanAdvisor', label: t.featureChips.humanAdvisor });
  if (service.copyTrading)
    featureChips.push({ id: 'copyTrading', label: t.featureChips.copyTrading });
  if (service.fractionalShares)
    featureChips.push({ id: 'fractionalShares', label: t.featureChips.fractionalShares });
  if (service.goalPlanning)
    featureChips.push({ id: 'goalPlanning', label: t.featureChips.goalPlanning });

  // Resolve focus chip label from translations when we have a canonical id.
  const focusLabel =
    service.focusId &&
    (t.focusOptions[service.focusId as keyof typeof t.focusOptions] ?? null);

  // Resolve service-type chip label (only when a canonical id is set).
  const serviceTypeLabel =
    service.serviceType &&
    t.serviceTypeOptions[
      service.serviceType as keyof typeof t.serviceTypeOptions
    ];

  return (
    <article className="rounded-xl border border-gray-200 bg-white p-5 shadow-[0_1px_3px_rgb(15_23_42_/_0.1)] sm:p-6">
      {/* Identity row */}
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white p-1 text-base font-bold text-blue-900 sm:h-14 sm:w-14">
          {service.logoUrl ? (
            <Image
              src={service.logoUrl}
              alt={service.title}
              width={56}
              height={56}
              className="h-full w-full rounded-lg object-contain"
              unoptimized
            />
          ) : (
            <span>{service.title.slice(0, 2)}</span>
          )}
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-extrabold tracking-tight text-gray-900 sm:text-[19px]">
              {service.title}
            </h3>
            {serviceTypeLabel && (
              <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-[2px] text-[10px] font-bold uppercase tracking-wide text-purple-800">
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
                {serviceTypeLabel}
              </span>
            )}
            {focusLabel && (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-[2px] text-[10px] font-bold uppercase tracking-wide text-blue-800">
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="6" />
                  <circle cx="12" cy="12" r="2" />
                </svg>
                {focusLabel}
              </span>
            )}
            {service.fiLicensed && (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-[2px] text-[10px] font-bold uppercase tracking-wide text-green-800">
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  aria-hidden="true"
                >
                  <path d="M12 2 4 6v6c0 5 3.5 9 8 10 4.5-1 8-5 8-10V6l-8-4z" />
                </svg>
                {t.fiLicense}
              </span>
            )}
          </div>
          {service.tagline && (
            <p className="mt-0.5 text-sm text-gray-500">{service.tagline}</p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <InfoTooltip text={t.ratingTooltip} />
            <span className="inline-flex gap-px">
              {[1, 2, 3, 4, 5].map((i) => (
                <span key={i}>{STAR_SVG(i <= Math.round(service.appRatingNumber))}</span>
              ))}
            </span>
            <span className="text-xs text-gray-500">
              <strong className="text-gray-900">
                {service.appRatingNumber.toFixed(1)}
              </strong>
              /5
              {service.yearCreated ? ` · ${t.since} ${service.yearCreated}` : ''}
            </span>
          </div>
        </div>
      </div>

      {service.reviewText && (
        <p className="mt-4 text-sm leading-relaxed text-gray-700">
          {service.reviewText}
        </p>
      )}

      {metrics.length > 0 && (
        <div className={`mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 ${lgColsClass}`}>
          {metrics.map((m) => (
            <Metric
              key={m.label}
              label={m.label}
              value={m.value}
              note={m.note}
              tooltip={m.tooltip}
            />
          ))}
        </div>
      )}

      {/* Strengths + weaknesses + offering tags */}
      <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-3">
        {service.keyFeatures.length > 0 && (
          <div>
            <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">
              {t.keyStrengths}
            </div>
            <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
              {service.keyFeatures.map((f, i) => (
                <li key={i} className="flex gap-2 text-sm text-gray-700">
                  <svg
                    className="mt-0.5 shrink-0"
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#16a34a"
                    strokeWidth="3"
                    aria-hidden="true"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {service.keyDownsides.length > 0 && (
          <div>
            <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">
              {t.keyWeaknesses}
            </div>
            <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
              {service.keyDownsides.map((d, i) => (
                <li key={i} className="flex gap-2 text-sm text-gray-700">
                  <svg
                    className="mt-0.5 shrink-0"
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#dc2626"
                    strokeWidth="3"
                    aria-hidden="true"
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">
            {t.availableAt.replace('{service}', service.title)}
          </div>
          <div className="flex flex-wrap gap-1">
            {service.accountTypes.map((id) => (
              <span
                key={`acct-${id}`}
                className="rounded-full px-2 py-[3px] text-[11px] font-semibold"
                style={{ background: `${ACCENT}12`, color: ACCENT }}
              >
                {t.accountTypeOptions[
                  id as keyof typeof t.accountTypeOptions
                ] ?? id}
              </span>
            ))}
            {service.assetClasses.map((id) => (
              <span
                key={`asset-${id}`}
                className="rounded-full bg-gray-100 px-2 py-[3px] text-[11px] font-semibold text-gray-700"
              >
                {t.assetClassOptions[
                  id as keyof typeof t.assetClassOptions
                ] ?? id}
              </span>
            ))}
          </div>
        </div>
      </div>

      {featureChips.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {featureChips.map((chip) => (
            <span
              key={chip.id}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] font-medium text-gray-700"
            >
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#16a34a"
                strokeWidth="3"
                aria-hidden="true"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {chip.label}
            </span>
          ))}
        </div>
      )}

      {(service.securityText ||
        service.taxText ||
        service.strategyText ||
        service.pricingText) && (
        <div className="mt-4 flex flex-col gap-1.5">
          {service.strategyText && (
            <ComplianceLine
              label={t.strategyLabel}
              text={service.strategyText}
              icon={
                <>
                  <line x1="12" y1="2" x2="12" y2="22" />
                  <polyline points="6 8 12 2 18 8" />
                </>
              }
              stroke="#7c3aed"
            />
          )}
          {service.pricingText && (
            <ComplianceLine
              label={t.pricingLabel}
              text={service.pricingText}
              icon={
                <>
                  <circle cx="12" cy="12" r="10" />
                  <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
                  <line x1="12" y1="6" x2="12" y2="8" />
                  <line x1="12" y1="16" x2="12" y2="18" />
                </>
              }
              stroke="#0ea5e9"
            />
          )}
          {service.securityText && (
            <ComplianceLine
              label={t.securityLabel}
              text={service.securityText}
              icon={<path d="M12 2 4 6v6c0 5 3.5 9 8 10 4.5-1 8-5 8-10V6l-8-4z" />}
              stroke="#16a34a"
            />
          )}
          {service.taxText && (
            <ComplianceLine
              label={t.taxLabel}
              text={service.taxText}
              icon={
                <>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="8" y1="13" x2="16" y2="13" />
                  <line x1="8" y1="17" x2="13" y2="17" />
                </>
              }
              stroke="#1d4ed8"
            />
          )}
        </div>
      )}

      {service.offerText && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2.5 text-xs leading-relaxed text-orange-900">
          <svg
            className="mt-px shrink-0"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#c2410c"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
            <line x1="7" y1="7" x2="7.01" y2="7" />
          </svg>
          <span>
            <strong className="font-semibold">{t.offerLabel}:</strong>{' '}
            {service.offerText}
          </span>
        </div>
      )}

      {/* CTA row */}
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
        {service.affiliateCtaUrl && (
          <a
            href={service.affiliateCtaUrl}
            target="_blank"
            rel="noopener sponsored"
            className={
              service.offerText
                ? 'group inline-flex items-center gap-1.5 rounded-lg bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white no-underline shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:bg-orange-600 hover:text-white hover:shadow-md active:translate-y-0'
                : 'group inline-flex items-center gap-1.5 rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white no-underline shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:bg-blue-800 hover:text-white hover:shadow-md active:translate-y-0'
            }
          >
            {service.offerText
              ? t.claimOffer.replace('{service}', service.title)
              : service.affiliateCtaLabel ||
                t.openAccount.replace('{service}', service.title)}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
              className="transition-transform duration-150 group-hover:translate-x-0.5"
            >
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </a>
        )}
        {reviewCta && (
          <a
            href={reviewCta.url}
            className="group inline-flex items-center gap-1 px-3 py-2.5 text-sm font-semibold text-blue-700 no-underline transition-colors hover:text-blue-900"
          >
            <span className="group-hover:underline">{reviewCta.label}</span>
            <span
              aria-hidden="true"
              className="inline-block transition-transform duration-150 group-hover:translate-x-0.5"
            >
              →
            </span>
          </a>
        )}
      </div>
    </article>
  );
}

function ComplianceLine({
  label,
  text,
  icon,
  stroke,
}: {
  label?: string;
  text: string;
  icon: React.ReactNode;
  stroke: string;
}) {
  return (
    <div className="flex items-start gap-2 rounded-lg bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-600">
      <svg
        className="mt-px shrink-0"
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke={stroke}
        strokeWidth="2"
        aria-hidden="true"
      >
        {icon}
      </svg>
      <span>
        {label && <strong className="font-semibold text-gray-800">{label}: </strong>}
        {text}
      </span>
    </div>
  );
}

function InfoTooltip({ text }: { text: string }) {
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        tabIndex={0}
        aria-label={text}
        className="peer flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-gray-300 text-[10px] font-bold text-gray-500 transition-colors hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
      >
        i
      </button>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-0 top-full z-10 mt-1.5 w-56 rounded-md bg-gray-900 px-2.5 py-1.5 text-[11px] font-medium leading-snug text-white opacity-0 shadow-lg transition-opacity peer-hover:opacity-100 peer-focus:opacity-100"
      >
        {text}
      </span>
    </span>
  );
}

function Metric({
  label,
  value,
  note,
  tooltip,
}: {
  label: string;
  value: string;
  note?: string;
  tooltip?: string;
}) {
  // Build a native tooltip combining label + value + (clipped) note + an
  // optional disclaimer (used for historical-return metric). Hovering any
  // part of the box reveals the full context — the note is visually
  // truncated to two lines.
  const tooltipParts = [
    `${label}: ${value}`,
    note ? `\n${note}` : '',
    tooltip ? `\n\n${tooltip}` : '',
  ].filter(Boolean);
  return (
    <div
      className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5"
      title={tooltipParts.join('')}
    >
      <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
        {label}
      </div>
      <div className="text-[15px] font-bold text-gray-900">{value}</div>
      {note && (
        <div
          className="mt-0.5 text-[11px] leading-snug text-gray-600 line-clamp-2"
          title={note}
        >
          {note}
        </div>
      )}
    </div>
  );
}
